import { FC, useContext, useEffect } from "react";
import { Controller } from "react-hook-form";
import { format, parse } from "date-fns";
import Select, { GroupBase, MultiValue, OptionsOrGroups, PropsValue, SingleValue } from "react-select";
import { useDebouncedCallback } from "use-debounce";
import DatePicker from "react-datepicker";
import ReactInputMask from "react-input-mask";

import HubInputField from "../inputType";
import AddCaseSiteOfCare from "../addCase/addCaseSiteOfCare";
import useFetch from "src/hooks/useFetch";

import { APIEndpoints } from "src/types/apiTypes";
import { DiagnosisResponseProps, CaseStepOneProps, ProcedureRequestProps, SiteOfCareResponseType } from "./types";
import { ProductRequestProps } from "../manufacturer/statistics/types";
import { DRUG_QUANTITY_133mg, DRUG_QUANTITY_266mg } from "src/constants/cases";
import { AuthContext } from "src/context/authContext";
import CustomDatePicker from "../customDatePicker";



const CaseStepOne: FC<CaseStepOneProps> = ({ control, isVisible, watch, getValues, setValue }): JSX.Element => {
  const { userData } = useContext(AuthContext);

  const diagnosisCode = watch("prescription_attributes.diagnosis_code");
  const procedure_cpt = watch("procedure_attributes.cpt_code");
  const drugName = watch("prescription_attributes.drug_name");



  const { data: allProducts } = useFetch<ProductRequestProps[]>(APIEndpoints.GET_PRODUCTS);



  const { data: siteOfCareApiResponse } =
    useFetch<SiteOfCareResponseType>(APIEndpoints.GET_SITE_OF_CARE_DROPDOWN);

  useEffect(() => {
    if (siteOfCareApiResponse && siteOfCareApiResponse.data && siteOfCareApiResponse.data.length > 0 && userData) {
      if (!getValues("shipments_attributes.0.practice_id")) {
        const userPractice = siteOfCareApiResponse.data.find((soc) => soc.name === userData.practice_name)
        if (userPractice) {
          setValue("shipments_attributes.0.practice_id", userPractice.id)
          setValue("shipments_attributes.0.practice_name", userPractice.name)
          setValue("shipments_attributes.0.practice_type", userPractice.practice_type || "");
          setValue("shipments_attributes.0.ship_to_street", userPractice.address.street)
          setValue("shipments_attributes.0.ship_to_suite", userPractice.address.suite || "")
          setValue("shipments_attributes.0.ship_to_city", userPractice.address.city)
          setValue("shipments_attributes.0.ship_to_state", userPractice.address.state)
          setValue("shipments_attributes.0.ship_to_zip", userPractice.address.zip)
          setValue("shipments_attributes.0.ship_to", "other");
          setValue("shipments_attributes.0.ship_to_npi", userPractice.npi)
          !(typeof userPractice.phone === "string" && userPractice.phone !== "N/A") &&
            setValue("shipments_attributes.0.phone", (userPractice.phone as number).toString())
          !(typeof userPractice.fax === "string" && userPractice.fax !== "N/A") &&
            setValue("shipments_attributes.0.fax", userPractice.fax.toString())
          userPractice.email !== "N/A" && setValue("shipments_attributes.0.email", userPractice.email)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteOfCareApiResponse]);



  /**
   * @GetProcedureListing API to call getProcedureListing
   */
  const { loading: procedureLoader, data: procedureListing, mutate: getProcedureListing, removeErrorAndData: clearProcedureData } =
    useFetch<ProcedureRequestProps>(APIEndpoints.GET_PROCEDURE_DROPDOWN);

  const searchProcedure = (search: string) => {
    if (typeof search === "string" && search.length >= 2) {
      clearProcedureData();
      getProcedureListing(null, null, { query: search })
    }
  }

  const debouncedProcedureCall = useDebouncedCallback(searchProcedure, 1500);

  useEffect(() => {
    if (procedure_cpt || (procedureListing && procedureListing.data)) {
      setTimeout(() => clearProcedureData(), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procedure_cpt]);





  /**
   * @SearchDiagnosisBySearchValue search by API for diagnosis
   * @first initialize useFetch() with diagnosis
   * @second search diagnosis by search term
   */
  const { loading: diagnosisLoader, data: rfcListing, mutate: getDiagnosis, removeErrorAndData: clearDiagnosisData } =
    useFetch<DiagnosisResponseProps>(APIEndpoints.GET_DIAGNOSIS_DROPDOWN);

  /** @second */
  const searchDiagnosis = (search: string) => {
    if (typeof search === "string" && search.length >= 2) {
      clearDiagnosisData();
      getDiagnosis(null, null, { search });
    }
  };

  const debounced = useDebouncedCallback(searchDiagnosis, 1500);

  useEffect(() => {
    if (diagnosisCode || (rfcListing && rfcListing.data)) {
      setTimeout(() => clearDiagnosisData(), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagnosisCode]);





  return (
    <div className={`card mb-3 ${!isVisible ? 'd-none' : ''}`}>
      <div className="card-header">
        <h4 className="m-0">Getting Started</h4>
      </div>

      <div className="card-body">
        <p>
          In order to process your request for benefit investigation, the following
          information must be completed. Please fill out the fields below as completely as possible.
          Missing fields may result in a delay to processing patient benefits in a timely manner.
        </p>

        <div className="row">
          <div className="col-12 col-lg-6">
            <Controller
              name="prescription_attributes.drug_name"
              control={control}
              defaultValue=""
              render={({field, fieldState: {error}}) => (
                <HubInputField
                  inputId="prescription_attributes.drug_name"
                  isRequired={true}
                  isRowBased={true}
                  isRowInsideAnother={true}
                  inputLabel="Product"
                  field={field}
                  error={error}
                  isDropdown={true}
                  isSearchable={false}
                  isTypeAhead
                  dropdownData={
                    (allProducts && allProducts.length > 0 ?
                      allProducts.map((prod) => ({label:prod.name.replace(/^exparel/i, "EXPAREL"), value: prod.name}))
                      : []
                    ) as unknown as OptionsOrGroups<string, GroupBase<string>>
                  }
                />
              )}
            />

            <Controller
              name="prescription_attributes.qty"
              control={control}
              defaultValue={0}
              render={({ field, fieldState: { error } }) => (
                <div className="row mb-3">
                  <label
                    htmlFor="prescription_attributes.qty"
                    className="form-label col-lg-4 col-md-4 col-12"
                  >
                    Qty *
                  </label>

                  <div className="col-lg-8 col-md-8 col-12">
                    <Select
                      isSearchable={false}
                      className="form-control w-max p-0 border-0"
                      styles={{
                        control: (baseStyles) => ({
                          ...baseStyles,
                          borderBottomRightRadius: 0,
                          borderTopRightRadius: 0,
                        })
                      }}
                      placeholder="Select Quantity..."
                      options={
                        (drugName === "Exparel 133mg/10ml vial" ? DRUG_QUANTITY_133mg : DRUG_QUANTITY_266mg) as unknown as OptionsOrGroups<number, GroupBase<number>>
                      }
                      {...field}
                      value={((drugName === "Exparel 133mg/10ml vial" ? DRUG_QUANTITY_133mg : DRUG_QUANTITY_266mg).find((qty) => qty.value === field.value) || 0) as unknown as PropsValue<number>}
                      onChange={(value) => {
                        field.onChange((value as unknown as {
                          value: number;
                          label: string;
                        }).value);
                      }}
                    />

                    {error && error.message ? (
                      <div dangerouslySetInnerHTML={{ __html: error.message }} className="text-danger text-small mt-2"></div>
                    ) : null}
                  </div>
                </div>
              )}
            />

            <div className="row mb-3">
              <label htmlFor="procedure_attributes.cpt_code" className="form-label col-lg-4 col-md-4 col-12">
                Procedure (CPT/CDT) *
              </label>
                <Controller
                  name="procedure_attributes.cpt_code"
                  control={control}
                  defaultValue=""
                  render={({field, fieldState: {error}}) => (
                    <div className="col-lg-8 col-md-8 col-12">
                      <Select
                        options={
                          (procedureListing && procedureListing.data && procedureListing.data.length > 0 ?
                            procedureListing.data.map((option) => ({
                              value: option.code,
                              label: `${option.code}-${option.description}`
                            }))
                            : []) as unknown as OptionsOrGroups<string, GroupBase<string>>
                        }
                        id="procedure_attributes.cpt_code"
                        isSearchable={true}
                        placeholder="Enter Procedure CPT..."
                        {...field}
                        noOptionsMessage={() => procedureLoader ? "Loading..." : "Please enter 2 or more characters"}
                        value={
                          (field.value && {
                            value: field.value.split,
                            label: field.value
                          }) as unknown as SingleValue<string>
                        }
                        onChange={(val) => {
                          field.onChange((val as unknown as { value: string; label: string }).value)
                        }}
                        onInputChange={debouncedProcedureCall}
                      />

                      {error && error.message ? (
                        <div className="text-danger text-small mt-2">{error.message}</div>
                      ) : null}
                    </div>
                  )}
                />
            </div>

            <div className="row mb-3">
                <label htmlFor="prescription_attributes.diagnosis_code"
                        className="form-label col-lg-4 col-md-4 col-12">
                    Diagnosis Code *
                </label>
                <div className="col-lg-8 col-md-8 col-12">
                    <Controller
                        name="prescription_attributes.diagnosis_code"
                        control={control}
                        defaultValue={[]}
                        render={({field, fieldState: {error}}) => (
                            <>
                                <Select
                                    options={
                                        (rfcListing && rfcListing.data && rfcListing.data.length > 0 ?
                                            rfcListing.data.map((option) => ({
                                                value: option.code,
                                                label: option.fullname
                                            }))
                                            : []) as unknown as OptionsOrGroups<string, GroupBase<string>>
                                    }
                                    id="prescription_attributes.diagnosis_code"
                                    isSearchable={true}
                                    isMulti={true}
                                    placeholder="Enter Diagnosis Code"
                                    {...field}
                                    noOptionsMessage={() => diagnosisLoader ? "Loading..." : "Please enter 2 or more characters"}
                                    value={
                                        field.value.map((item) => ({
                                            value: item,
                                            label: item,
                                        })) as unknown as MultiValue<unknown>
                                    }
                                    onChange={
                                        (val) => {
                                            field.onChange(val.map((item: unknown) => (item as {
                                                value: string;
                                            }).value))
                                        }
                                    }
                                    onInputChange={debounced}
                                />

                                {error && error.message ? (
                                    <div className="text-danger text-small mt-2">{error.message}</div>
                                ) : null}
                            </>
                        )}
                    />
                </div>
            </div>

            <Controller
              name="prescription_attributes.surgery"
              control={control}
              defaultValue=""
              render={({ field, fieldState: { error } }) =>
                <HubInputField
                  field={field}
                  inputId="ac"
                  isRequired={false}
                  isRowBased={true}
                  isRowInsideAnother={true}
                  inputLabel="Surgical Procedure"
                  error={error}
                  // isTypeAhead

                />
              }
            />

            <div className="notes-field">
              <Controller
                name="prescription_attributes.note"
                control={control}
                defaultValue=""
                render={({field, fieldState: {error}}) => (
                  <div className="row mb-3">
                    <label
                      htmlFor="prescription_attributes.note"
                      className="form-label col-lg-4 col-md-4 col-12"
                    >
                      Notes
                    </label>
                    <div className="col-lg-8 col-md-8 col-12">
                      <textarea
                        {...field} id="prescription_attributes.note"
                        className="form-control border border-none" style={{resize: "none"}}
                        rows={3}
                      />
                      {error && error.message ? (
                        <div dangerouslySetInnerHTML={{__html: error.message}}
                          className="text-danger text-small mt-2"></div>
                      ) : null}
                    </div>
                  </div>
                )}
              />
            </div>
          </div>

          <div className="col-lg-6">
            <Controller
              name="prescription_attributes.direction"
              control={control}
              render={({field, fieldState: {error}}) => (
                <div className="row mb-3">
                  <label htmlFor="prescription_attributes.direction" className="form-label col-lg-4 col-md-4 col-12">
                    Indication For Use
                  </label>

                  <div className="col-lg-8 col-md-8 col-12">
                    <textarea {...field} readOnly disabled id="prescription_attributes.direction"
                      className="form-control border border-none" style={{resize: "none"}}
                      rows={8}
                    />
                    {error && error.message ? (
                      <div dangerouslySetInnerHTML={{__html: error.message}}
                        className="text-danger text-small mt-2"></div>
                    ) : null}
                  </div>
                </div>
              )}
            />

            <Controller
              name="prescription_attributes.treatment_date"
              control={control}
              defaultValue=""
              render={({field, fieldState: {error}}) => (
                <div className="row mb-3">
                  <label htmlFor="prescription_attributes.treatment_date" className="form-label col-lg-4 col-md-4 col-12">
                    Treatment Date
                  </label>
                  <div className="col-lg-8 col-md-8 col-12">
                     <CustomDatePicker
                        field={{ ...field, value: field.value || "" }}
                        minDate={new Date()} // Prevent past dates
                        //maxDate={new Date()} // Restrict to current year
                        id="prescription_attributes.treatment_date"
                        className={`form-control ${error && error.message ? 'is-invalid' : ''}`}
                        placeholderText="mm/dd/yyyy"
                        error={error}
                        wrapperClassName="w-100"
                      />
                     
                                        
                    {error && error.message ? (
                      <div dangerouslySetInnerHTML={{__html: error.message}} className="text-danger text-small mt-2 "></div>
                    ) : null}
                      {/* <DatePicker
                          selected={
                              field.value
                                  ? parse( field.value, "yyyy-MM-dd", new Date() ) // Parse the date from the default format if value exists
                                  : null
                          }
                          onChange={( date ) => {
                              field.onChange( date ? format( date, "yyyy-MM-dd" ) : "" ); // Format back to yyyy-MM-dd
                          }}
                          dateFormat="MM/dd/yyyy" // Display format
                          className={`form-control ${error?.message ? "is-invalid" : ""}`}
                          placeholderText="mm/dd/yyyy"
                          showMonthDropdown
                          showYearDropdown
                          customInput={
                              <ReactInputMask mask="99/99/9999">
                                  {( inputProps: {} ) => <input {...inputProps} />}
                              </ReactInputMask>
                          }
                          wrapperClassName="w-100"
                          minDate={new Date()} // Restrict past dates
                      />
                      <i
                          className="bi bi-calendar position-absolute"
                          style={{
                              right: error?.message ? "50px" : "25px", // Move left on validation error
                              top: "18px",
                              transform: "translateY(-50%)",
                              pointerEvents: "none",
                          }}
                      ></i>

                    {error && error.message ? (
                      <div dangerouslySetInnerHTML={{__html: error.message}} className="text-danger text-small mt-2 "></div>
                    ) : null} */}
                  </div>
                </div>
              )}
            />

            <Controller
              name="shipments_attributes.0.practice_id"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <HubInputField
                  inputId="shipments_attributes.0.practice_id"
                  inputLabel="Site of Care"
                  isRequired={true}
                  isRowBased={true}
                  isRowInsideAnother={true}
                  error={error}
                  field={field}
                  isDropdown={true}
                  isSearchable={false}
                  isTypeAhead
                  dropdownData={
                    (siteOfCareApiResponse && siteOfCareApiResponse.data && siteOfCareApiResponse.data.length > 0 ?
                      [{ value: 0, label: 'New...' }, ...siteOfCareApiResponse.data.map((siteOfCare) => ({ value: siteOfCare.id, label: siteOfCare.name }))]
                      : [{ value: 0, label: 'New...' }]) as unknown as OptionsOrGroups<string, GroupBase<string>>
                  }
                />
              )}
            />
          </div>
        </div>

        <AddCaseSiteOfCare control={control} getValues={getValues} setValue={setValue} watch={watch} socListing={siteOfCareApiResponse} />

        <Controller
          name="prescription_attributes.written_date"
          control={control}
          defaultValue={format(new Date(), 'MM/dd/yyyy')}
          render={() => <></>}
        />
      </div>
    </div>
  );
};

export default CaseStepOne;
